import { useMemo } from 'react';

import AuthService from 'src/services/api/auth.service';
import {
  useActiveAssignmentsViewSelector,
  useActiveMSAssignmentsStatus,
  useActiveMSAssignmentsSubject,
  useActiveUserClassesSelector,
  useSelectedClassSelector,
} from 'src/store/features';
import { useAssignmentsXApiPayloadSelector } from 'src/store/features/assignments-xapi-payload.slice';
import { getClassesBks } from 'src/utils/classes.utils';

import { useFeedback } from './use-feedback.hook';
import { useIsMiddleSchoolView } from './use-is-middle-school-view';

export type CommonXApiPayload = {
  view: string;
  currentStatusFilters?: string[];
  currentTotalAssignmentsMarkedDone?: number;
  currentDisplayedAssignments: number;
  currentTotalAssignmentsWithFeedback?: number;
  currentProductFilters?: string[];
  currentTotalAssignments: number;
  currentClassesDisplayed?: string[];
  activeSection?: string;
};

export const useCommonXapiContextPayloadHook = (): CommonXApiPayload => {
  const isStudent = AuthService.isStudent();

  const isMiddleSchoolView = useIsMiddleSchoolView();

  const {
    assignmentsTotalCount,
    displayedAssignmentsCount,
    doneAssignmentsCount,
  } = useAssignmentsXApiPayloadSelector();

  const activeMSAssignmentsStatus = useActiveMSAssignmentsStatus();
  const activeMSAssignmentsSubject = useActiveMSAssignmentsSubject();
  const { feedbacksCount } = useFeedback();
  const activeAssignmentsView = useActiveAssignmentsViewSelector();

  const selectedClass = useSelectedClassSelector();
  const activeUserClasses = useActiveUserClassesSelector();

  const currentClassesAssignmentsAreDisplayedFor = useMemo(() => {
    if (isStudent || !selectedClass || selectedClass.length > 1)
      return getClassesBks(activeUserClasses);

    if (!selectedClass.length) return;

    return [selectedClass[0].sourcedId];
  }, [activeUserClasses, isStudent, selectedClass]);

  return {
    view: isMiddleSchoolView ? 'middle-school' : 'elementary-school',
    currentDisplayedAssignments: displayedAssignmentsCount,
    currentClassesDisplayed: currentClassesAssignmentsAreDisplayedFor,
    currentProductFilters:
      isMiddleSchoolView && activeMSAssignmentsSubject
        ? [activeMSAssignmentsSubject]
        : undefined,
    currentStatusFilters:
      isMiddleSchoolView && activeMSAssignmentsStatus
        ? [activeMSAssignmentsStatus]
        : undefined,
    currentTotalAssignments: assignmentsTotalCount,
    currentTotalAssignmentsMarkedDone: !isMiddleSchoolView
      ? doneAssignmentsCount
      : undefined,
    currentTotalAssignmentsWithFeedback: isMiddleSchoolView
      ? feedbacksCount
      : undefined,
    activeSection: !isMiddleSchoolView ? activeAssignmentsView : undefined,
  };
};
